import React, { useState, useEffect, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import FeedbackIcon from "@mui/icons-material/Feedback";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Rating from "@mui/material/Rating";
import CommentIcon from "@mui/icons-material/Comment";
import { API_post_store_copilot_feedback } from "../../../../../constants";
import { postRightworkAPI } from "../../../requests/utilities/requests";
import { formatDate } from "../../../requests/utilities/helpers";
import { analytics } from "../../../../../utilities/requests";

function CopilotButton(props) {
  const [isOpen, setIsOpen] = useState(false);
  const [dots, setDots] = useState("");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const dropdownRef = useRef(null);

  // Animate the dots
  useEffect(() => {
    if (props.isCopilotOn) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length === 3 ? "" : prev + "."));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [props.isCopilotOn]);

  const toggleDropdown = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (newOpenState) {
      analytics.track("copilot_enabled", props.store_id, {});
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const modifiedCopilotShifts = props.copilotShifts?.map((shift) => ({
    ...shift,
    action: "add",
  }));

  const modifiedCopilotDeleteShifts = props.copilotDeleteShifts?.map(
    (shift) => ({
      ...shift,
      action: "delete",
    })
  );

  const hasRecommendations =
    props.copilotShifts?.length > 0 || props.copilotDeleteShifts?.length > 0;

  const openFeedbackModal = () => {
    setIsOpen(false); // Close the dropdown
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = async () => {
    // Prepare data to send
    const requestData = {
      store_id: props.store_id,
      stars: feedbackRating,
      feedback_text: feedbackComment,
      week_start_date: formatDate(props.week_start_date),
    };

    try {
      setIsFeedbackSubmitting(true);

      const res = await postRightworkAPI(
        API_post_store_copilot_feedback,
        requestData
      );

      if (res.status === 200) {
        setIsFeedbackSubmitting(false);
        setIsSubmissionSuccessful(true);

        // Close after showing success message for 2 seconds
        setTimeout(() => {
          closeFeedbackModal();
        }, 2000);
      }
    } catch (error) {
      console.error(error.response?.data?.system?.message);
      setIsFeedbackSubmitting(false);
      // You could also add error state handling here
    }
  };

  // Update closeFeedbackModal to reset all states
  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setFeedbackRating(0);
    setFeedbackComment("");
    setIsSubmissionSuccessful(false); // Reset success state
  };

  const hideRecommendations = () => {
    setIsOpen(false);
    props.setIsCopilotOn(false);
  };

  const pulseKeyframes = `
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }
`;

  // Add the keyframes to the document's style
  useEffect(() => {
    if (typeof document !== "undefined") {
      const style = document.createElement("style");
      style.innerHTML = `
        ${pulseKeyframes}
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <div>
      <div className="relative" ref={dropdownRef}>
        {props.week_ended || !props.autosched_available ? (
          <Tooltip
            title={
              props.week_ended
                ? "Copilot not available for past weeks"
                : "Copilot not available"
            }
          >
            <div className="text-[#bdbdbd] w-10 h-10 flex items-center justify-center">
              <AutoAwesomeIcon />
            </div>
          </Tooltip>
        ) : (
          <div className="relative group">
            <div className="flex items-center space-x-1">
              <Tooltip
                title={`${
                  !isOpen
                    ? props.isCopilotOn
                      ? "Copilot Enabled"
                      : "Open Copilot"
                    : ""
                }`}
              >
                <span>
                  <IconButton
                    onClick={toggleDropdown}
                    style={{
                      color: props.isCopilotOn ? "#6d28d9" : undefined,
                      animation:
                        props.isCopilotOn && props.isCopilotLoading
                          ? "pulse 2s infinite"
                          : "none",
                    }}
                    sx={{ p: 1 }}
                    className="hover:bg-gray-200 transition"
                    disabled={props.week_ended || !props.autosched_available}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <div
                className="h-6 flex items-center whitespace-nowrap"
                onClick={toggleDropdown}
              >
                {props.isCopilotOn && (
                  <div>
                    {props.isCopilotLoading ? (
                      <p className="text-xs xl:text-[10px] text-violet-700 cursor-pointer">
                        Updating{dots}
                      </p>
                    ) : (
                      <div>
                        {props.copilotShifts?.length > 0 && (
                          <p className="text-xs xl:text-[10px] text-violet-700 cursor-pointer">
                            +{props.copilotShifts?.length} shifts {props.copilotDeleteShifts?.length === 0 ? "recommended" : ""}
                          </p>
                        )}
                        {props.copilotDeleteShifts?.length > 0 && (
                          <p className="text-xs xl:text-[10px] text-violet-700 cursor-pointer">
                            -{props.copilotDeleteShifts?.length} shifts recommended
                          </p>
                        )}
                        {props.copilotShifts?.length === 0 &&
                          props.copilotDeleteShifts?.length === 0 && (
                            <p className="text-xs xl:text-[10px] text-violet-700 cursor-pointer">
                              No recommendations
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Updated Modern Dropdown */}
            <div
              className={`absolute left-0 w-[300px] mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-40 transition-all duration-200 origin-top-left ${
                isOpen
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95 pointer-events-none"
              }`}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-4 pt-4 pb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {props.isCopilotOn ? "AI Recommendations" : "AI Copilot"}
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100 transition-colors"
                >
                  <CloseIcon style={{ fontSize: "18px" }} />
                </button>
              </div>

              {props.isCopilotOn ? (
                <div className="px-4 pb-4">
                  {/* Recommendations mode */}
                  <div className="text-xs text-gray-500 mb-4">
                    {props.isCopilotLoading
                      ? "Analyzing schedule and generating optimal recommendations..."
                      : hasRecommendations
                      ? "To accept a new shift, press and hold. To remove a shift, click the X."
                      : "No schedule changes are recommended at this time."}
                  </div>

                  {/* Stats summary - only show if we have recommendations */}
                  {/* {hasRecommendations && !props.isCopilotLoading && (
                    <div className="flex mb-4 text-xs">
                      {props.copilotShifts?.length > 0 && (
                        <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded-md flex items-center mr-2 flex-grow">
                          <span className="font-medium">+{props.copilotShifts.length}</span>
                          <span className="ml-1">new shifts</span>
                        </div>
                      )}
                      {props.copilotDeleteShifts?.length > 0 && (
                        <div className="bg-rose-50 text-rose-700 px-3 py-2 rounded-md flex items-center flex-grow">
                          <span className="font-medium">-{props.copilotDeleteShifts.length}</span>
                          <span className="ml-1">shifts to remove</span>
                        </div>
                      )}
                    </div>
                  )} */}

                  {/* Action buttons */}
                  <div className="space-y-2">
                    {/* Only show accept all button if we have recommendations */}
                    {hasRecommendations && !props.isCopilotLoading && (
                      <button
                        onClick={() => {
                          props.setIsAddingAllCopilotShiftsLoading(true);
                          props.addMultipleShifts([
                            ...modifiedCopilotShifts,
                            ...modifiedCopilotDeleteShifts,
                          ]);
                          analytics.track(
                            "copilot_accept_all_recs",
                            props.store_id,
                            {
                              modified_count: modifiedCopilotShifts.length,
                              deleted_count: modifiedCopilotDeleteShifts.length,
                            }
                          );
                        }}
                        disabled={props.isAddingAllCopilotShiftsLoading}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md transition-colors flex justify-center items-center"
                      >
                        {props.isAddingAllCopilotShiftsLoading ? (
                          <div className="flex items-center">
                            <CircularProgress
                              size={16}
                              sx={{
                                color: "white",
                                marginRight: "8px",
                              }}
                            />
                            <span>Accepting All</span>
                          </div>
                        ) : (
                          "Accept All Recommendations"
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => hideRecommendations()}
                      className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md transition-colors"
                    >
                      Hide Recommendations
                    </button>

                    {/* Feedback button */}
                    <div className="w-full flex justify-center">
                      <button
                        onClick={openFeedbackModal}
                        className="pt-1.5 pb-0.5 text-violet-600 text-xs font-medium rounded-md transition-colors flex justify-center items-center"
                      >
                        <FeedbackIcon sx={{ fontSize: 14, marginRight: 1 }} />
                        Give Feedback
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="px-4 pb-4">
                  {/* Intro mode */}
                  <div className="inline-block px-2 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded-full mb-3">
                    NEW
                  </div>

                  <div className="mb-4">
                    <p className="text-gray-600 text-xs mb-2">
                      The AI Copilot recommends shifts that fill your demand as
                      efficiently as possible.
                    </p>
                    <p className="text-gray-600 text-xs">
                      Accounts for availability, overtime, compliance, and
                      target hours.
                    </p>
                  </div>

                  <button
                    onClick={() => props.setIsCopilotOn(true)}
                    className="w-full py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    Show Recommendations
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Redesigned Feedback Modal */}
      <Modal
        open={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
        aria-labelledby="feedback-modal-title"
      >
        <Box sx={modalStyle}>
          {/* Close button in top right corner */}
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={closeFeedbackModal}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            {/* Header with icon */}
            <div className="mb-6 flex items-center space-x-3">
              <FeedbackIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                AI Copilot Feedback
              </h3>
            </div>

            {/* Description text */}
            <div className="mb-6">
              <p className="text-gray-600 text-sm">
                Your feedback helps us improve our AI recommendations. Please
                rate your experience and provide any comments.
              </p>
            </div>

            {/* Rating section */}
            <div className="mb-6 flex flex-col items-center bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700 text-sm font-medium mb-2">
                How helpful were the recommendations?
              </p>
              <Rating
                value={feedbackRating}
                onChange={(event, newValue) => {
                  setFeedbackRating(newValue);
                }}
                size="large"
              />
            </div>

            {/* Comments section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Comments
              </label>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="What could be improved with the recommendations? Please be as specific as possible."
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    paddingY: "10px",
                    paddingX: "14px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                    fontSize: "15px",
                  },
                }}
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              {isSubmissionSuccessful ? (
                <div className="w-full text-center py-2.5 bg-green-100 text-green-700 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Feedback submitted successfully!
                </div>
              ) : (
                <>
                  <button
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                    onClick={closeFeedbackModal}
                    disabled={isFeedbackSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                      ${
                        feedbackRating > 0
                          ? "bg-violet-600 text-white hover:bg-violet-700"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                    onClick={() => feedbackRating > 0 && handleFeedbackSubmit()}
                    disabled={feedbackRating === 0 || isFeedbackSubmitting}
                  >
                    {isFeedbackSubmitting ? (
                      <div className="flex items-center justify-center">
                        <CircularProgress
                          size={16}
                          sx={{ color: "white", marginRight: "8px" }}
                        />
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      "Submit Feedback"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default CopilotButton;
